"use client"

import { FormEvent, useState } from "react"

import { API_BASE_URL } from "@lib/config"

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i

const Newsletter = () => {
  const [email, setEmail] = useState("")
  const [status, setStatus] = useState<
    "idle" | "loading" | "success" | "error"
  >("idle")
  const [message, setMessage] = useState<string | null>(null)

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setMessage(null)

    const normalizedEmail = email.trim()
    if (!EMAIL_REGEX.test(normalizedEmail)) {
      setStatus("error")
      setMessage("Ingresá un correo electrónico válido.")
      return
    }

    setStatus("loading")

    const fallbackError = "No pudimos registrar tu correo. Intenta nuevamente."

    try {
      const response = await fetch(
        `${API_BASE_URL}/public/newsletter/subscribers`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: normalizedEmail }),
        }
      )

      let data: { error?: string; alreadySubscribed?: boolean } | null = null
      try {
        data = await response.json()
      } catch {
        data = null
      }

      if (!response.ok) {
        const errorMessage = data?.error || fallbackError
        throw new Error(errorMessage)
      }

      setStatus("success")
      if (data?.alreadySubscribed) {
        setMessage(
          "Este correo ya está suscripto. ¡Gracias por seguir con nosotros!"
        )
      } else {
        setMessage(
          "¡Gracias por suscribirte! Pronto recibirás nuestras novedades."
        )
      }
      setEmail("")
    } catch (error) {
      setStatus("error")
      setMessage(error instanceof Error ? error.message : fallbackError)
    }
  }

  const isLoading = status === "loading"
  const isSuccess = status === "success"

  return (
    <div className="w-full bg-black py-20 px-6">
      <div className="mx-auto flex max-w-5xl flex-col gap-10 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-col gap-3">
          <p className="text-[10px] font-lealti tracking-[0.22em] uppercase text-gray-500">
            Newsletter
          </p>
          <h2 className="text-2xl md:text-[2.5rem] font-lealti-light leading-tight text-white max-w-sm">
            Recibí descuentos
            <br />
            exclusivos en tu correo
          </h2>
        </div>

        <div className="flex w-full max-w-md flex-col gap-4">
          <form
            className="flex flex-col gap-0 sm:flex-row sm:items-stretch border-b border-gray-700 focus-within:border-white transition-colors"
            onSubmit={handleSubmit}
            noValidate
          >
            <input
              type="email"
              inputMode="email"
              placeholder="Tu correo electrónico"
              className="flex-1 border-0 bg-transparent py-4 text-sm font-lealti-light text-white placeholder-gray-600 outline-none"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
              aria-label="Correo electrónico"
              disabled={isLoading}
            />
            <button
              type="submit"
              disabled={isLoading}
              className="shrink-0 bg-transparent py-4 pl-4 text-[11px] font-lealti tracking-[0.18em] uppercase text-gray-400 transition hover:text-white disabled:opacity-40"
            >
              {isLoading ? "Enviando…" : "Suscribirme →"}
            </button>
          </form>
          <div aria-live="polite" className="min-h-[1rem]">
            {message && (
              <p
                className={`text-xs font-lealti-light ${
                  isSuccess ? "text-gray-400" : "text-red-400"
                }`}
              >
                {message}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Newsletter
